/**
 * Safely parse a timestamp value that may arrive as a Date object or an ISO
 * string (with or without a timezone suffix).
 *
 * - If the value is already a valid Date, return it directly.
 * - If it's a string, try parsing as-is first.
 * - Fall back to appending "Z" only when no offset is present and the direct
 *   parse failed, to handle bare UTC strings from some backends.
 * - Returns null for any value that cannot be parsed into a valid date.
 */
export function parseLastSeenTimestamp(
  value?: Date | string | null,
): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  const hasOffset = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
  if (!hasOffset) {
    const fallback = new Date(`${value}Z`);
    if (!Number.isNaN(fallback.getTime())) {
      return fallback;
    }
  }

  return null;
}
